import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import {
    SERVICE_UNAVAILABLE_REASON,
    VIDEO_SERVICE_UNAVAILABLE_MESSAGE,
} from "../../../constants/video-service";
import LoadingScreen from "../../../screens/LoadingScreen";
import { getAuthHeader } from "../../../utils/authHeader";
import { ListingType, UserListing } from "../../../types/userListing";
import { Images } from "../../../types/listingImages";
import { ListingVideos } from "../../../types/listingVideos";
import { CarouselApi } from "../../misc/Carousel";
import { Assets } from "../../../types/asset-type";
import axios from "axios";
import Slideshow from "../slideshow/Slideshow";
import ListingHeader from "../listing/ListingHeader";
import DataLoadingError from "../../misc/DataLoadingError";
import MediaCarousel from "../../misc/MediaCarousel";
import { CombinedImage, CombinedMedia, CombinedVideo } from "../listing/types";
import TabsComponent from "../listing/Tabs";
import CampaignCard from "../listing/CampaignCard";
import ImageTagCard from "../listing/ImageTagCard";
import SlideshowGeneratorCard from "../listing/SlideshowGeneratorCard";
import { AllListings } from "../../../types/allListings";
import { useSocket } from "../../../context/SocketContext";
import { CampaignProgress } from "../../campaign/types";
import UserNotesCard from "../listing/UserNotesCard";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useAutopilotEnabled } from "../../../hooks/useAutopilotEnabled";
import { JobStatus } from "../slideshow";
import { ImCross } from "react-icons/im";
import { FiAlertCircle } from "react-icons/fi";
import NonMLSWarning from "./NonMLSWarning";
import OpenHousePosts from "./OpenHousePosts";
import NewOpenHousePost from "../../misc/custom-posts/open-house/ScheduleOpenHousePosts";
import VideoPosts from "./VideoPosts";
import NewVideoPost from "../../misc/custom-posts/video-post/NewVideoPost";
import VideoConversionErrorDialog from "../../misc/VideoConversionError";
import { syncDetails } from "./tabs/types";

interface LocationState {
    listingId: string;
}

interface ListingViewProps {
    mlsApproved: boolean | null;
}

const ListingView: React.FC<ListingViewProps> = ({ mlsApproved }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state as LocationState | undefined;
    const searchParams = new URLSearchParams(location.search);
    // MB-1520: the header's Grouped | Separate control only matters under
    // Autopilot - grouping never changes a manual campaign.
    const { autopilotEnabled } = useAutopilotEnabled();
    const listingId = searchParams.get('id') || state?.listingId;

    const progressTimeoutRef = useRef<number | null>(null);
    const videoProgressTimeoutRef = useRef<number | null>(null);

    const [isGenerating, setIsGenerating] = useState(false);
    const [propertyData, setPropertyData] = useState<UserListing | null>(null);
    const [originalPropertyData, setOriginalPropertyData] =
        useState<UserListing | null>(null);
    const [allListing, setAllListing] = useState<AllListings | null>(null);
    const [images, setImages] = useState<Images[]>([]);
    const [videos, setVideos] = useState<ListingVideos[]>([]);
    const [errorVideos, setErrorVideos] = useState<ListingVideos[]>([]);
    const [assets, setAssets] = useState<Assets[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
    const [isSlideshowOpen, setIsSlideshowOpen] = useState(false);
    const [isOpenHousePostOpen, setIsOpenHousePostOpen] = useState(false);
    const [videoPostsOpen, setVideoPostsOpen] = useState(false);

    // Refs to track previous dialog states for detecting closes
    const prevOpenHouseOpen = useRef(isOpenHousePostOpen);
    const prevVideoPostOpen = useRef(videoPostsOpen);

    const [jobId, setJobId] = useState<string | null>(null);
    const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
    const [jobListingId, setJobListingId] = useState<string | null>(null);
    const [isJobInProgress, setIsJobInProgress] = useState(false);
    const [showSaveWarning, setShowSaveWarning] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const [activeTab, setActiveTab] = useState<"images" | "videos" | "details">(
        "details"
    );
    const [syncDetails, setSyncDetails] = useState<syncDetails | null>(null);

    const [displayed, setDisplayed] = useState<boolean>(false);
    const displayedRef = useRef<boolean>(false); // Ref to track dismissal
    const [convertError, setConvertError] = useState(false);

    const [carouselIndices, setCarouselIndices] = useState<{
        images: number;
        videos: number;
        details: number;
    }>({
        images: 0,
        videos: 0,
        details: 0,
    });

    const isMobile = useIsMobile(1300);

    const { socket, isAuthenticated } = useSocket(); // Ensure `isAuthenticated` is available
    const subscribedCampaignsRef = useRef<Set<string>>(new Set());
    const subscribedVideosRef = useRef<Set<string>>(new Set());
    const subscribedListingSyncsRef = useRef<Set<string>>(new Set());
    const openSlideshow = () => setIsSlideshowOpen(true);
    const closeSlideshow = () => setIsSlideshowOpen(false);

    const [nonMlsExists, setNonMlsExists] = useState(false);

    const [errorVideosDialogOpen, setErrorVideosDialogOpen] =
        useState<boolean>(false);

    useEffect(() => {
        if (errorVideos.length > 0) {
            setErrorVideosDialogOpen(true);
        }
    }, [errorVideos]);

    // Keep usableImageCount in sync with same-session exclude toggles so the
    // create-campaign warning reflects exclusions made without a page reload.
    useEffect(() => {
        if (!propertyData?.images) return;
        const usableImageCount = propertyData.images.filter(
            (image) => !image.excludeFromPosts
        ).length;
        setAllListing((prev) =>
            prev ? { ...prev, usableImageCount } : prev
        );
    }, [propertyData?.images]);

    const fetchListing = useCallback(async () => {
        try {
            if (listingId) {
                // Clear images and videos before fetching
                // setImages([]);
                // setVideos([]);

                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;

                const response = await fetch(
                    `${backendUrl}/listing/${listingId}`,
                    {
                        headers,
                        method: "GET",
                    }
                );

                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }

                const data = await response.json();

                console.log("Fetched listing data:", data);

                if (data) {
                    const updatedData: UserListing = data;
                    setPropertyData(updatedData);
                    setOriginalPropertyData(updatedData);

                    const allListingData: AllListings = {
                        id: data._id,
                        formattedAddress: data.formattedAddress,
                        status: data.status,
                        latitude: data.latitude,
                        longitude: data.longitude,
                        primaryImage: data.primaryImage,
                        campaignStatus: data.campaignStatus,
                        campaignType: data.campaignType,
                        managedByAutopilot: data.managedByAutopilot,
                        generatedPosts: data.generatedPosts,
                        publishedPosts: data.publishedPosts,
                        scheduledPosts: data.scheduledPosts,
                        unapprovedPosts: data.unapprovedPosts,
                        generatingVideoCount: data.generatingVideoCount,
                        autoVideoPostsTotal: data.autoVideoPostsTotal,
                        autoVideoPostsDone: data.autoVideoPostsDone,
                        contentRefresh: data.contentRefresh,
                        progress: data.progress,
                        color: data.color,
                        campaignId: data.campaignId,
                        campaignProgress: data.campaignProgress,
                        imageCount: data.imageCount,
                        usableImageCount: data.usableImageCount,
                        listingType: data.listingType,
                        convertError: data.convertError,
                        parsedFooter: data.parsedFooter,
                        parsedGoogleFooter: data.parsedGoogleFooter,
                        sharedListing: data.sharedListing,
                        marketingCategory: data.marketingCategory,
                        pricingOverride: data.pricingOverride,
                        campaignPauseReason: data.campaignPauseReason,
                    };

                    console.log("All listing data:", allListingData);

                    setAllListing(allListingData);

                    const shouldShowNonMLSWarning =
                        allListingData?.campaignStatus?.toLowerCase() !==
                            "processing" &&
                        mlsApproved === true &&
                        allListingData.listingType !== ListingType.MLSListing &&
                        !displayedRef.current; // Use ref

                    setNonMlsExists(shouldShowNonMLSWarning);

                    setConvertError(allListingData.convertError || false);

                    setImages(data.images || []);

                    setVideos(data.videos || []);
                }
            } else {
                toast.error(
                    <CustomToast
                        message="Listing Retrieval Failed"
                        type="error"
                    />,
                    {
                        autoClose: 3000,
                    }
                );

                setTimeout(() => {
                    navigate("/listings");
                }, 3000);
            }
        } catch (error) {
            console.error("Error fetching listing:", error);
            toast.error(
                <CustomToast message="Failed to load listing." type="error" />,
                {
                    autoClose: 3000,
                }
            );
        }
    }, [listingId, navigate, mlsApproved]);

    const fetchAssets = useCallback(async () => {
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const response = await axios.get(
                `${backendUrl}/assets/image-assets`,
                {
                    headers,
                }
            );

            if (response.data) {
                if (Array.isArray(response.data)) {
                    setAssets(response.data);
                } else if (
                    response.data.assets &&
                    Array.isArray(response.data.assets)
                ) {
                    setAssets(response.data.assets);
                } else {
                    console.error("Assets data is not an array", response.data);
                    setAssets([]);
                }
            }
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 404) {
                    // Corrected to access status via error.response
                } else {
                    console.error("Error fetching assets:", error);
                    toast.error(
                        <CustomToast
                            message="Failed to load assets."
                            type="error"
                        />,
                        {
                            autoClose: 3000,
                        }
                    );
                }
            } else {
                toast.error(
                    <CustomToast
                        message="Failed to load assets."
                        type="error"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
            }
        }
    }, []);

    const fetchSlideshowJob = useCallback(async () => {
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const response = await axios.get(
                `${backendUrl}/listing/slideshow-jobs`,
                {
                    headers,
                }
            );

            if (response.status === 200) {
                const {
                    jobId: fetchedJobId,
                    jobStatus: fetchedJobStatus,
                    listingId,
                } = response.data;

                if (fetchedJobId && fetchedJobId.length > 0) {
                    setJobId(fetchedJobId);
                    setJobStatus(fetchedJobStatus);
                    setJobListingId(listingId);
                }

                // Optionally, handle listingId if needed
            } else {
                throw new Error("Error fetching slideshow jobs");
            }
        } catch (error) {
            console.error("Error fetching slideshow jobs:", error);
            // Optionally, handle errors (e.g., toast notifications)
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([
                    fetchListing(),
                    fetchAssets(),
                    fetchSlideshowJob(),
                ]);
            } catch (error) {
                setIsLoading(true);
                toast.error(
                    <CustomToast message="Failed to load data." type="error" />,
                    {
                        autoClose: 3000,
                    }
                );
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [fetchListing, fetchAssets, fetchSlideshowJob]);

    // Refetch listing when dialogs close after post creation
    useEffect(() => {
        // Check if Open House dialog just closed
        if (prevOpenHouseOpen.current && !isOpenHousePostOpen) {
            fetchListing();
        }
        
        // Check if Video Post dialog just closed
        if (prevVideoPostOpen.current && !videoPostsOpen) {
            fetchListing();
        }
        
        // Update refs for next render
        prevOpenHouseOpen.current = isOpenHousePostOpen;
        prevVideoPostOpen.current = videoPostsOpen;
    }, [isOpenHousePostOpen, videoPostsOpen, fetchListing]);

    useEffect(() => {
        setIsJobInProgress(
            jobId !== null &&
                (jobStatus === JobStatus.QUEUED ||
                    jobStatus === JobStatus.PROCESSING)
        );
    }, [jobId, jobStatus, jobListingId, propertyData]);

    // A campaign auto-video is in flight (mirrors the "Generating Video"
    // indicator in CampaignStatusCard) — block manual slideshow creation while
    // it runs, just like an in-progress slideshow job does.
    const isGeneratingVideo = (allListing?.generatingVideoCount ?? 0) > 0;

    // Extract campaignId and campaignStatus using useMemo for stability
    const campaignId = useMemo(() => allListing?.campaignId, [allListing]);
    const campaignStatus = useMemo(
        () => allListing?.campaignStatus,
        [allListing]
    );

    const stopProgressSilenceWatcher = useCallback(() => {
        if (progressTimeoutRef.current !== null) {
            clearTimeout(progressTimeoutRef.current);
            progressTimeoutRef.current = null;
        }
    }, []);

    const startProgressSilenceWatcher = useCallback(() => {
        stopProgressSilenceWatcher();
        // only schedule if we're still processing
        if (allListing?.campaignStatus === "PROCESSING") {
            progressTimeoutRef.current = window.setTimeout(() => {
                // re-fetch and re-arm
                fetchListing().finally(startProgressSilenceWatcher);
            }, 5000);
        }
    }, [allListing?.campaignStatus, fetchListing, stopProgressSilenceWatcher]);

    const stopVideoProgressWatcher = useCallback(() => {
        if (videoProgressTimeoutRef.current !== null) {
            clearTimeout(videoProgressTimeoutRef.current);
            videoProgressTimeoutRef.current = null;
        }
    }, []);

    const startVideoProgressWatcher = useCallback(() => {
        stopVideoProgressWatcher();

        const hasProcessingVideos = videos.some((video) => video.isProcessing);

        if (hasProcessingVideos) {
            videoProgressTimeoutRef.current = window.setTimeout(() => {
                fetchListing().finally(startVideoProgressWatcher); // Refetch listing and restart loop
            }, 30000); // 30 seconds
        }
    }, [videos, fetchListing, stopVideoProgressWatcher]);

    useEffect(() => {
        const hasProcessingVideos = videos.some((video) => video.isProcessing);

        if (hasProcessingVideos) {
            startVideoProgressWatcher();
        } else {
            stopVideoProgressWatcher();
        }

        return () => {
            stopVideoProgressWatcher();
        };
    }, [videos, startVideoProgressWatcher, stopVideoProgressWatcher]);

    // Fallback poll for slideshow completion. Completion normally arrives over
    // the socket (Slideshow.tsx), but a dropped/recycled socket can miss that
    // one-shot event, leaving the job stuck "Generating" until a manual refresh.
    // While a job is in progress, poll the authoritative job status; once it is
    // no longer active the listing is refetched to pick up the new video. The
    // socket stays the fast path — whichever clears the job first wins (both are
    // idempotent), so this only kicks in when the event was missed.
    useEffect(() => {
        if (!isJobInProgress) return;

        let cancelled = false;
        let timeoutId: number;

        const poll = async () => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const response = await axios.get(
                    `${backendUrl}/listing/slideshow-jobs`,
                    { headers }
                );

                const activeJobId: string | null = response.data?.jobId;
                if (!cancelled && (!activeJobId || activeJobId.length === 0)) {
                    setJobId(null);
                    setJobStatus(null);
                    await fetchListing();
                    return;
                }
            } catch (error) {
                console.error("Error polling slideshow job:", error);
            }

            if (!cancelled) {
                timeoutId = window.setTimeout(poll, 30000);
            }
        };

        timeoutId = window.setTimeout(poll, 30000);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [isJobInProgress, fetchListing]);

    useEffect(() => {
        if (campaignStatus === "PROCESSING") {
            startProgressSilenceWatcher();
        } else {
            stopProgressSilenceWatcher();
        }
        // clean up on unmount
        return stopProgressSilenceWatcher;
    }, [
        campaignStatus,
        startProgressSilenceWatcher,
        stopProgressSilenceWatcher,
    ]);

    useEffect(() => {
        if (!socket || !isAuthenticated) return;

        const currentSubscribedCampaigns = subscribedCampaignsRef.current;
        const currentSubscribedVideos = subscribedVideosRef.current;
        const listingUpdates = subscribedListingSyncsRef.current;

        // Only subscribe if the campaign is in "PROCESSING" status and not already subscribed
        if (campaignId) {
            if (
                campaignStatus === "PROCESSING" &&
                !currentSubscribedCampaigns.has(campaignId)
            ) {
                socket.emit("subscribeToCampaign", campaignId);
                currentSubscribedCampaigns.add(campaignId);
            }
        }

        for (const video of videos) {
            if (
                video.isProcessing &&
                !currentSubscribedVideos.has(video.name)
            ) {
                socket.emit("subscribeToVideoConvert", video.name);
                currentSubscribedVideos.add(video.name);
            }
        }

        if (propertyData && propertyData._id) {
            socket.emit("subscribeToListingSync", propertyData._id);
            listingUpdates.add(propertyData._id);
        }

        const handleProgressUpdate = (data: {
            entityId: string;
            entityType: string;
            progress: CampaignProgress | string;
            reason?: string;
        }) => {
            if (
                data.entityType === "campaign" &&
                data.entityId === campaignId
            ) {
                setAllListing((prevAllListing) => {
                    if (!prevAllListing) return prevAllListing;

                    const updatedCampaignStatus =
                        typeof data.progress !== "string" &&
                        data.progress.currentStep === "Completed"
                            ? "ACTIVE"
                            : prevAllListing.campaignStatus;

                    return {
                        ...prevAllListing,
                        campaignProgress:
                            typeof data.progress === "string"
                                ? prevAllListing.campaignProgress
                                : data.progress,
                        campaignStatus: updatedCampaignStatus,
                    };
                });

                if (
                    typeof data.progress !== "string" &&
                    (data.progress.currentStep === "Completed" ||
                        data.progress.currentStep.toLowerCase() === "error")
                ) {
                    fetchListing();
                }
                startProgressSilenceWatcher();
            } else if (data.entityType === "video-convert") {
                handleVideoProcessUpdate({
                    entityId: data.entityId,
                    entityType: data.entityType,
                    status:
                        typeof data.progress !== "string"
                            ? data.progress.currentStep
                            : data.progress,
                    reason: data.reason,
                });
            } else if (data.entityType === "listing-sync") {
                handleListingSyncUpdate({
                    entityId: data.entityId,
                    entityType: data.entityType,
                    status:
                        typeof data.progress !== "string"
                            ? data.progress.currentStep
                            : data.progress,
                });
            }
        };

        const handleVideoProcessUpdate = (data: {
            entityId: string;
            entityType: "video-convert";
            status: string;
            reason?: string;
        }) => {
            if (data.entityType === "video-convert") {
                // MB-1396: the api's failure emitter sends JobStatus.FAILED
                // ("failed"); "error" is kept for the legacy step relay. When
                // the failure is the video service being unreachable, say so.
                const isFailure = ["error", "failed"].includes(
                    data.status.toLowerCase()
                );
                if (
                    isFailure &&
                    data.reason === SERVICE_UNAVAILABLE_REASON
                ) {
                    toast.error(
                        <CustomToast
                            message={VIDEO_SERVICE_UNAVAILABLE_MESSAGE}
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                }
                const videos = propertyData?.videos;

                if (videos && videos?.length > 0) {
                    const video = videos.find(
                        (thisVideo) => thisVideo.name === data.entityId
                    );

                    if (video) {
                        if (isFailure) {
                             const updatedVideo = {
                                ...video,
                                isProcessing: false,
                                error: true,
                            };

                            setErrorVideos((prevErrorVideos) => [
                                ...prevErrorVideos,
                                updatedVideo,
                            ]);

                            setVideos((prevVideos) =>
                                prevVideos.filter(
                                    (v) => v.url !== data.entityId
                                )
                            );
                        } else if (data.status.toLowerCase() === "completed") {
                            const updatedVideo = {
                                ...video,
                                isProcessing: false,
                                error: false,
                            };

                            setVideos((prevVideos) => {
                                const filteredVideos = prevVideos.filter(
                                    (v) => v.url !== data.entityId
                                );
                                return [updatedVideo, ...filteredVideos];
                            });
                        }
                    }
                }
            }
        };

        const handleListingSyncUpdate = (data: {
            entityId: string;
            entityType: "listing-sync";
            status: string;
        }) => {
            if (
                data.entityType === "listing-sync" &&
                data.status !== "in_progress"
            ) {
                fetchListing();
            }
        };

        // WebSocket listeners for real-time updates
        const handleCampaignUpdated = (eventData: any) => {
            if (eventData.resourceType === "campaign" && eventData.action === "updated") {
                fetchListing();
            }
        };

        // Autopilot creates campaigns server-side (no browser call to react to);
        // refetch so the new Processing campaign shows and gets its progress
        // subscription without a manual page refresh.
        const handleCampaignCreated = (eventData: any) => {
            if (eventData.resourceType === "campaign" && eventData.action === "created") {
                fetchListing();
            }
        };

        const handleCampaignDeleted = (eventData: any) => {
            if (eventData.resourceType === "campaign" && eventData.action === "deleted") {
                fetchListing();
            }
        };

        const handlePostUpdated = (eventData: any) => {
            if (eventData.resourceType === "post" && eventData.action === "updated") {
                fetchListing();
            }
        };

        // Auto-slideshow renders inject 1–2 video posts at once; debounce so a
        // multi-platform render triggers a single refetch instead of one per post.
        let postCreatedTimer: ReturnType<typeof setTimeout> | null = null;
        const handlePostCreated = (eventData: any) => {
            if (eventData.resourceType === "post" && eventData.action === "created") {
                if (postCreatedTimer) clearTimeout(postCreatedTimer);
                postCreatedTimer = setTimeout(() => {
                    fetchListing();
                }, 800);
            }
        };

        const handlePostDeleted = (eventData: any) => {
            if (eventData.resourceType === "post" && eventData.action === "deleted") {
                fetchListing();
            }
        };

        const handleListingDeleted = (eventData: any) => {
            if (eventData.resourceType === "listing" && eventData.action === "deleted") {
                const deletedListingId = String(eventData.resourceId);
                const currentListingId = String(propertyData?._id || propertyData?.id || listingId);
                
                // Check if the deleted listing is the current listing being viewed
                if (deletedListingId === currentListingId) {
                    navigate('/listings');
                }
            }
        };

        const handleListingUpdated = (eventData: any) => {
            if (eventData.resourceType === "listing" && eventData.action === "updated") {
               fetchListing();
            }
        };

        // Listen for progress updates
        socket.on("progressUpdate", handleProgressUpdate);
        socket.on("campaign:created", handleCampaignCreated);
        socket.on("campaign:updated", handleCampaignUpdated);
        socket.on("campaign:deleted", handleCampaignDeleted);
        socket.on("post:updated", handlePostUpdated);
        socket.on("post:created", handlePostCreated);
        socket.on("post:deleted", handlePostDeleted);
        socket.on("listing:deleted", handleListingDeleted);
        socket.on("listing:updated", handleListingUpdated);

        // Cleanup function
        return () => {
            // Remove the progress update listener
            socket.off("progressUpdate", handleProgressUpdate);
            socket.off("campaign:created", handleCampaignCreated);
            socket.off("campaign:updated", handleCampaignUpdated);
            socket.off("campaign:deleted", handleCampaignDeleted);
            socket.off("post:updated", handlePostUpdated);
            socket.off("post:created", handlePostCreated);
            socket.off("post:deleted", handlePostDeleted);
            socket.off("listing:deleted", handleListingDeleted);
            socket.off("listing:updated", handleListingUpdated);
            if (postCreatedTimer) clearTimeout(postCreatedTimer);
            startProgressSilenceWatcher();
            // Unsubscribe only if the campaign was subscribed in this effect
            if (
                campaignId &&
                campaignStatus === "PROCESSING" &&
                currentSubscribedCampaigns.has(campaignId)
            ) {
                socket.emit("unsubscribeFromCampaign", campaignId);
                currentSubscribedCampaigns.delete(campaignId);
            }

            // Unsubscribe from video processing updates
            for (const video of videos) {
                if (video.isProcessing) {
                    socket.emit("unsubscribeFromVideoConvert", video.name);
                }
            }
        };
    }, [
        socket,
        isAuthenticated,
        campaignId,
        campaignStatus,
        fetchListing,
        propertyData?._id,
        videos,
        startProgressSilenceWatcher,
        propertyData,
        listingId,
        navigate,
    ]);

    const combinedMedia: CombinedMedia[] = [
        ...images.map(
            (image) => ({ ...image, type: "image" } as CombinedImage)
        ),
        ...videos.map(
            (video) => ({ ...video, type: "video" } as CombinedVideo)
        ),
    ];

    const combinedImages = useMemo(
        () =>
            images.map(
                (image) => ({ ...image, type: "image" } as CombinedImage)
            ),
        [images]
    );

    const combinedVideos = useMemo(
        () =>
            videos.map(
                (video) => ({ ...video, type: "video" } as CombinedVideo)
            ),
        [videos]
    );

    useEffect(() => {
        if (carouselApi) {
            const index = carouselIndices[activeTab] || 0;
            carouselApi.scrollTo(index);
        }
    }, [activeTab, carouselApi, carouselIndices]);

    if (isLoading) return <LoadingScreen />;

    if (!propertyData) {
        setTimeout(() => {
            navigate("/listings");
        }, 3000);
        return (
            <DataLoadingError
                heading="Data Loading Error"
                text="The requested property data could not be loaded. You will be
              redirected back in a few seconds."
            />
        );
    }

    const handleDeleteImages = async () => {
        setIsLoading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const videoIdsToDelete = errorVideos.map((video) => video.id);

            await axios.delete(`${backendUrl}/listing/videos/`, {
                headers: headers,
                data: {
                    listingId:
                        propertyData?.id.toString() ||
                        propertyData?._id.toString(),
                    listingType: propertyData?.listingType,
                    videos: videoIdsToDelete,
                },
            });
        } catch (error) {
            console.error("Error deleting images:", error);
        } finally {
            setErrorVideosDialogOpen(false);
            setErrorVideos([]);
            setIsLoading(false);
        }
    };

    const LISTING_STATUSES = [
        "Active",
        "ComingSoon",
        "ActiveUnderContract",
        "Pending",
        "Inactive"
    ] as const;

    return (
        <div className="container mx-auto px-4 py-8">
            {nonMlsExists && !displayed && (
                <NonMLSWarning
                    listingId={propertyData.id || propertyData._id.toString()}
                    open={nonMlsExists && !displayed}
                    setOpen={(open: boolean) => {
                        if (!open) {
                            setDisplayed(true);
                            displayedRef.current = true;
                        }
                        setNonMlsExists(open);
                    }}
                    setDisplayed={(value: boolean) => {
                        setDisplayed(value);
                        if (value) {
                            displayedRef.current = true;
                        }
                    }}
                    convertError={convertError}
                    setConvertError={setConvertError}
                />
            )}
            {errorVideos.length > 0 && (
                <VideoConversionErrorDialog
                    videos={errorVideos}
                    open={errorVideosDialogOpen}
                    onClose={() => {
                        // mark it as viewed and close the dialog
                        handleDeleteImages();
                        setErrorVideosDialogOpen(false);
                    }}
                />
            )}
            <div className={`flex ${isMobile ? "flex-col" : "flex-row gap-8"}`}>
                {/* Left Column */}
                <div
                    className={`flex flex-col w-full ${
                        isMobile ? "" : "lg:flex-1 max-w-[70%]"
                    }`}
                >
                    {/* Listing Header */}
                    <ListingHeader
                        formattedAddress={propertyData.formattedAddress}
                        propertyType={propertyData.propertyType}
                        status={propertyData.status}
                        isSyncing={propertyData.isSyncing || false}
                        marketingCategory={propertyData.marketingCategory}
                        mlsPropertySubType={propertyData.mlsPropertySubType}
                        mlsPropertyType={propertyData.mlsPropertyType}
                        propertyGroup={propertyData.propertyGroup}
                        listingId={propertyData._id}
                        canEditGroup={autopilotEnabled === true}
                        autopilotEnabled={autopilotEnabled}
                        onGroupChanged={fetchListing}
                        managedByAutopilot={allListing?.managedByAutopilot}
                    />
                    {/* Media Carousel */}
                    <MediaCarousel
                        key={activeTab}
                        setCarouselApi={setCarouselApi}
                        combinedMedia={
                            activeTab === "details"
                                ? combinedMedia
                                : activeTab === "images"
                                ? combinedImages
                                : combinedVideos
                        }
                        isMobile={isMobile}
                        latitude={propertyData.latitude}
                        longitude={propertyData.longitude}
                        address={propertyData.formattedAddress}
                        addressHidden={
                            propertyData.InternetAddressDisplayYN === false
                        }
                    />
                    {/* Image Tag Card (Mobile Only) */}
                    {isMobile && (
                        <ImageTagCard
                            combinedMedia={combinedMedia}
                            carouselApi={carouselApi}
                            propertyData={propertyData}
                            setImages={setImages}
                            images={images}
                            setPropertyData={setPropertyData}
                        />
                    )}
                    {/* Tabs Component */}
                    <TabsComponent
                        images={images}
                        videos={videos}
                        setIsLoading={setIsLoading}
                        carouselApi={carouselApi}
                        propertyData={propertyData}
                        fetchListing={fetchListing}
                        fetchAssets={fetchAssets}
                        setPropertyData={setPropertyData}
                        originalPropertyData={originalPropertyData}
                        setOriginalPropertyData={setOriginalPropertyData}
                        combinedMedia={combinedMedia}
                        combinedImages={combinedImages}
                        combinedVideos={combinedVideos}
                        mlsApproved={mlsApproved}
                        setShowSaveWarning={setShowSaveWarning}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        carouselIndices={carouselIndices}
                        setCarouselIndices={setCarouselIndices}
                        isUploading={isUploading}
                        setIsUploading={setIsUploading}
                        syncDetails={syncDetails}
                        setSyncDetails={setSyncDetails}
                    />

                    {showSaveWarning ? (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                            <div
                                className={`relative ${
                                    isMobile ? "w-full max-w-md" : "w-1/3"
                                } rounded-lg bg-white p-6 shadow-lg`}
                            >
                                <button
                                    onClick={() => setShowSaveWarning(false)}
                                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    aria-label="Close"
                                >
                                    <ImCross className="h-6 w-6" />
                                </button>
                                <div className="flex flex-col items-center text-justify">
                                    <FiAlertCircle className="h-16 w-16 text-yellow-500" />
                                    <h2 className="mt-4 text-xl sm:text-2xl font-bold text-primary">
                                        Your Updates Have Been Saved
                                    </h2>
                                    <p className="mt-2 text-gray-600 text-sm sm:text-base">
                                        Any edits made to details will only
                                        apply to the next campaign renewal.
                                        Changes to details will not impact their
                                        use in any ongoing or existing campaign
                                        but will be reflected in future campaign
                                        renewals. Please review your edits
                                        carefully before saving.
                                    </p>
                                    <p className="mt-2 text-gray-600 text-sm sm:text-base">
                                        Please note that any changes to
                                        {propertyData.listingType ===
                                            "MLSListing" &&
                                            " Additional Content or"}{" "}
                                        Agent Details will be applied
                                        immediately to Ad Hoc features such as
                                        Add Post, Open House Posts, and Video
                                        Showcase.
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {syncDetails !== null ? (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                            <div
                                className={`relative ${
                                    isMobile ? "w-full max-w-md" : "w-1/3"
                                } rounded-lg bg-white p-6 shadow-lg`}
                            >
                                <button
                                    onClick={() => setSyncDetails(null)}
                                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 focus:outline-none"
                                    aria-label="Close"
                                >
                                    <ImCross className="h-6 w-6" />
                                </button>
                                <div className="flex flex-col items-center text-justify">
                                    <FiAlertCircle className="h-16 w-16 text-yellow-500" />
                                    <h2 className="mt-4 text-xl sm:text-2xl font-bold text-primary">
                                        {syncDetails.code === 209
                                            ? "No Updates Found for Your Listing"
                                            : "Updates Detected for Your Listing"}
                                    </h2>
                                    <p className="mt-2 text-gray-600 text-sm sm:text-base">
                                        {syncDetails.code === 209
                                            ? "We didn’t find any new updates for your listing at this time. MLS systems may take up to 60 minutes to reflect changes. We’ll automatically check again every hour and update your listing as soon as new information becomes available. Please check back later."
                                            : (() => {
                                                  const syncDate = new Date(
                                                      syncDetails.syncDate || ""
                                                  );
                                                  const now = new Date();
                                                  const timeDiff =
                                                      syncDate.getTime() -
                                                      now.getTime();
                                                  const isToday =
                                                      syncDate.toDateString() ===
                                                      now.toDateString();

                                                  if (timeDiff <= 60000) {
                                                      return "Good news! We’ve found updates for your listing, and the update process has just started.";
                                                  }

                                                  const formattedTime =
                                                      new Intl.DateTimeFormat(
                                                          "en-US",
                                                          {
                                                              hour: "numeric",
                                                              minute: "2-digit",
                                                              hour12: true,
                                                          }
                                                      ).format(syncDate);

                                                  if (isToday) {
                                                      return `We’ve found updates for your listing. It’s scheduled to be updated shortly today around ${formattedTime}.`;
                                                  }

                                                  const formattedDate =
                                                      new Intl.DateTimeFormat(
                                                          "en-US",
                                                          {
                                                              weekday: "long",
                                                              year: "numeric",
                                                              month: "long",
                                                              day: "numeric",
                                                              hour: "numeric",
                                                              minute: "2-digit",
                                                              hour12: true,
                                                          }
                                                      ).format(syncDate);

                                                  return `We’ve found updates for your listing. The update is scheduled for ${formattedDate}.`;
                                              })()}
                                    </p>

                                    {syncDetails.code !== 209 && (
                                        <>
                                            <p className="mt-2 text-gray-600 text-sm sm:text-base">
                                                Listings are automatically
                                                checked for updates every hour.
                                                If any new information is
                                                detected, your listing will be
                                                updated automatically.
                                            </p>
                                            <p className="mt-2 text-gray-600 text-sm sm:text-base">
                                                You’ll receive a notification in
                                                the bell icon at the top right
                                                of your screen once your listing
                                                has been updated.
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Right Column */}
                <div
                    className={`flex flex-col w-full ${
                        isMobile ? "gap-4" : "lg:w-1/3 max-w-[30%]"
                    } gap-8`}
                >
                    {/* Campaign Card */}
                    {allListing !== null &&
                        allListing.status !== undefined &&
                        LISTING_STATUSES.includes(
                            allListing?.status as
                                | "Active"
                                | "ComingSoon"
                                | "ActiveUnderContract"
                                | "Pending"
                                | "Inactive"
                        ) && (
                            <CampaignCard
                                allListing={allListing}
                                mlsApproved={mlsApproved || false}
                                isUploading={isUploading}
                                setLoading={setIsLoading}
                                setPropertyData={setPropertyData}
                                setOriginalPropertyData={
                                    setOriginalPropertyData
                                }
                                fetchListing={fetchListing}
                                isSyncing={propertyData?.isSyncing || false}
                                isVideo={videos.length > 0}
                                campaignQualifiedImageCount={
                                    propertyData?.campaignQualifiedImageCount
                                }
                                autoGenerateSlideshow={
                                    propertyData?.autoGenerateSlideshow
                                }
                                autoVideoUnavailableReason={
                                    propertyData?.autoVideoUnavailableReason
                                }
                            />
                        )}

                    {/* Image Tag Card (Desktop Only) */}
                    {!isMobile && (
                        <ImageTagCard
                            combinedMedia={combinedMedia}
                            carouselApi={carouselApi}
                            propertyData={propertyData}
                            setImages={setImages}
                            images={images}
                            setPropertyData={setPropertyData}
                        />
                    )}

                    <OpenHousePosts
                        listing={propertyData}
                        setIsOpen={setIsOpenHousePostOpen}
                        open={isOpenHousePostOpen}
                    />

                    {/* Slideshow Generator */}
                    <SlideshowGeneratorCard
                        openSlideshow={openSlideshow}
                        isGenerating={isGenerating}
                        isJobInProgress={isJobInProgress}
                        isUploading={isUploading}
                        isSyncing={propertyData?.isSyncing || false}
                        isGeneratingVideo={isGeneratingVideo}
                    />

                    <VideoPosts
                        listing={propertyData}
                        setIsOpen={setVideoPostsOpen}
                        open={videoPostsOpen}
                    />

                    {/* User Notes Card */}
                    <UserNotesCard
                        propertyData={propertyData}
                        setPropertyData={setPropertyData}
                        mlsApproved={mlsApproved || false}
                    />
                </div>
            </div>

            {/* Slideshow Component */}
            <Slideshow
                listingId={propertyData._id}
                listingType={propertyData.listingType}
                images={images}
                assets={assets}
                isOpen={isSlideshowOpen}
                onClose={closeSlideshow}
                formattedAddress={propertyData.formattedAddress}
                isGenerating={isGenerating}
                setIsGenerating={setIsGenerating}
                jobId={jobId}
                setJobId={setJobId}
                jobStatus={jobStatus}
                setJobStatus={setJobStatus}
                fetchListing={fetchListing}
                recentlyListed={propertyData.recentlyListed}
            />

            {/* Open House Posts */}
            {isOpenHousePostOpen && (
                <NewOpenHousePost
                    listing={propertyData}
                    setIsOpen={setIsOpenHousePostOpen}
                    open={isOpenHousePostOpen}
                />
            )}

            {/* Video Posts */}
            {videoPostsOpen && (
                <NewVideoPost
                    listing={propertyData}
                    setIsOpen={setVideoPostsOpen}
                    open={videoPostsOpen}
                />
            )}
        </div>
    );
};

export default ListingView;
